import clsx from 'clsx'
import { Text } from '../../../components/modules/ui'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const headerLabel = (headers, code, fallback) => {
  const header = Array.isArray(headers)
    ? headers.find(item => item.codigoFinal === code)
    : null

  return header?.nameFinal || fallback
}

const formatReportAmount = value => {
  const numericValue = Number(value ?? 0)
  const formattedValue = new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0,
  }).format(Math.abs(numericValue))

  return numericValue < 0 ? `(${formattedValue})` : formattedValue
}

const getAmountToneClass = value => {
  const amount = Number(value ?? 0)
  if (amount === 0) return 'text-slate-400 font-normal'
  return 'text-slate-700'
}

export default function Table({ rows, headers = [] }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!rows?.length) {
    return (
      <Text className='grid place-items-center py-12 text-muted font-sans'>
        No hay datos
      </Text>
    )
  }

  const sumByPrefix = prefix => {
    let periodo = 0
    let acumulado = 0

    for (const row of rows) {
      const code = row.codeCuenta ?? ''
      if (code.startsWith(prefix) && !row.isGroup) {
        periodo += Number(row.saldoPeriodo ?? 0)
        acumulado += Number(row.saldoAcumulado ?? 0)
      }
    }

    return { periodo, acumulado }
  }

  const totals = {
    operational: sumByPrefix('3-1-'),
    nonOperational: sumByPrefix('3-2-'),
  }

  const sectionNames = {
    operational: headerLabel(headers, '3-1-0-0', 'Resultado Operacional'),
    nonOperational: headerLabel(headers, '3-2-0-0', 'Resultado no Operacional'),
  }

  const displayRows = []
  let operationalHeaderInserted = false
  let nonOperationalHeaderInserted = false

  for (const row of rows) {
    const code = row.codeCuenta ?? ''

    if (!operationalHeaderInserted && code.startsWith('3-1-')) {
      displayRows.push({
        type: 'SECTION_HEADER',
        code: '3-1-0-0',
        name: sectionNames.operational,
        periodo: totals.operational.periodo,
        acumulado: totals.operational.acumulado,
      })
      operationalHeaderInserted = true
    }

    if (!nonOperationalHeaderInserted && code.startsWith('3-2-')) {
      displayRows.push({
        type: 'SECTION_HEADER',
        code: '3-2-0-0',
        name: sectionNames.nonOperational,
        periodo: totals.nonOperational.periodo,
        acumulado: totals.nonOperational.acumulado,
      })
      nonOperationalHeaderInserted = true
    }

    displayRows.push({ type: 'ROW', data: row })
  }

  const result = displayRows
    .filter(item => item.type === 'SECTION_HEADER')
    .reduce(
      (accumulator, section) => ({
        periodo: accumulator.periodo + Number(section.periodo ?? 0),
        acumulado: accumulator.acumulado + Number(section.acumulado ?? 0),
      }),
      { periodo: 0, acumulado: 0 }
    )

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className='rounded-lg border border-slate-200 bg-white px-2 py-2'>
        <div className='text-[11px] text-slate-500'>{label}</div>
        <div
          className={clsx(
            'text-sm font-semibold tabular-nums',
            getAmountToneClass(value)
          )}
        >
          {formatReportAmount(value)}
        </div>
      </div>
    )

    return (
      <div className='space-y-3 font-sans text-text'>
        {displayRows.map((item, index) => {
          if (item.type === 'SECTION_HEADER') {
            return (
              <div
                key={`header-${item.code}`}
                className='rounded-xl border border-slate-200 bg-slate-50 p-3'
              >
                <div className='text-xs font-bold text-slate-800'>{item.name}</div>
                <div className='mt-2 grid grid-cols-2 gap-2'>
                  {renderMetric('Saldo Periodo', item.periodo)}
                  {renderMetric('Saldo Acumulado', item.acumulado)}
                </div>
              </div>
            )
          }

          const row = item.data

          return (
            <div
              key={`${row?.codeCuenta ?? 'row'}-${index}`}
              className={clsx(
                'rounded-xl border border-slate-200 bg-white p-3',
                row?.isGroup && 'bg-slate-50'
              )}
            >
              <div
                className={clsx(
                  'text-xs',
                  row?.isGroup
                    ? 'font-bold text-slate-800'
                    : 'pl-5 font-normal text-slate-600'
                )}
              >
                {row.codeCuenta} {row.nameCuenta}
              </div>
              <div className='mt-2 grid grid-cols-2 gap-2'>
                {renderMetric('Saldo Periodo', row.saldoPeriodo)}
                {renderMetric('Saldo Acumulado', row.saldoAcumulado)}
              </div>
            </div>
          )
        })}

        <div className='rounded-xl border border-slate-300 bg-slate-50 p-3'>
          <div className='text-xs font-bold text-slate-900'>RESULTADO</div>
          <div className='mt-2 grid grid-cols-2 gap-2'>
            {renderMetric('Saldo Periodo', result.periodo)}
            {renderMetric('Saldo Acumulado', result.acumulado)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='relative max-h-[60vh] w-full overflow-y-auto overflow-x-auto rounded-lg border border-slate-200 bg-white font-sans text-[10px] leading-5 text-slate-700 sm:text-[11px]'>
      <table
        className='table-auto min-w-full border-separate border-spacing-0 text-[10px] sm:text-[11px]'
        style={{ tableLayout: 'auto' }}
      >
        <thead>
          <tr>
            <th className='sticky left-0 top-0 z-30 min-w-[240px] max-w-[280px] whitespace-normal break-words border-b border-slate-200 bg-white px-4 py-3 text-left text-[9px] font-bold uppercase tracking-wide text-slate-500 sm:text-[10px]'>
              CUENTA
            </th>
            <th className='sticky top-0 z-20 min-w-[120px] whitespace-nowrap border-b border-slate-200 bg-white px-3 py-3 text-right text-[9px] font-bold uppercase tracking-wide text-slate-500 sm:text-[10px]'>
              SALDO PERIODO
            </th>
            <th className='sticky top-0 z-20 min-w-[140px] whitespace-nowrap border-b border-l border-slate-200 bg-white px-3 py-3 text-right text-[9px] font-semibold uppercase tracking-wide text-slate-700 sm:text-[10px]'>
              SALDO ACUMULADO
            </th>
          </tr>
        </thead>

        <tbody>
          {displayRows.map((item, index) => {
            if (item.type === 'SECTION_HEADER') {
              return (
                <tr
                  key={`header-${item.code}`}
                  className='group bg-slate-50 hover:bg-slate-100/70'
                >
                  <td className='sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-b border-t border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-800 group-hover:bg-slate-100/70 sm:text-[11px]'>
                    {item.name}
                  </td>
                  <td
                    className={clsx(
                      'whitespace-nowrap border-b border-t border-slate-200 px-3 py-3 text-right text-[10px] font-bold tabular-nums sm:text-[11px]',
                      getAmountToneClass(item.periodo)
                    )}
                  >
                    {formatReportAmount(item.periodo)}
                  </td>
                  <td
                    className={clsx(
                      'whitespace-nowrap border-b border-l border-t border-slate-200 px-3 py-3 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                      getAmountToneClass(item.acumulado)
                    )}
                  >
                    {formatReportAmount(item.acumulado)}
                  </td>
                </tr>
              )
            }

            const row = item.data

            return (
              <tr
                key={`${row?.codeCuenta ?? 'row'}-${index}`}
                className={clsx(
                  'group bg-white hover:bg-slate-50',
                  row.isGroup
                    ? 'font-semibold text-slate-800'
                    : 'text-slate-600'
                )}
              >
                <td
                  className={clsx(
                    'sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-b border-slate-100 bg-white py-2.5 pr-4 text-left text-[10px] group-hover:bg-slate-50 sm:text-[11px]',
                    row.isGroup
                      ? 'pl-8 font-bold text-slate-800'
                      : 'pl-14 font-normal text-slate-600'
                  )}
                >
                  {row.codeCuenta} {row.nameCuenta}
                </td>
                <td
                  className={clsx(
                    'whitespace-nowrap border-b border-slate-100 px-3 py-2.5 text-right text-[10px] tabular-nums sm:text-[11px]',
                    row.isGroup && 'font-bold',
                    getAmountToneClass(row.saldoPeriodo)
                  )}
                >
                  {formatReportAmount(row.saldoPeriodo)}
                </td>
                <td
                  className={clsx(
                    'whitespace-nowrap border-b border-l border-slate-200 px-3 py-2.5 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                    getAmountToneClass(row.saldoAcumulado)
                  )}
                >
                  {formatReportAmount(row.saldoAcumulado)}
                </td>
              </tr>
            )
          })}

          <tr className='group bg-slate-50 hover:bg-slate-100/70'>
            <td className='sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-t border-slate-300 bg-slate-50 px-4 py-3 text-left text-[10px] font-bold text-slate-900 group-hover:bg-slate-100/70 sm:text-[11px]'>
              RESULTADO
            </td>
            <td
              className={clsx(
                'whitespace-nowrap border-t border-slate-300 px-3 py-3 text-right text-[10px] font-bold tabular-nums sm:text-[11px]',
                getAmountToneClass(result.periodo)
              )}
            >
              {formatReportAmount(result.periodo)}
            </td>
            <td
              className={clsx(
                'whitespace-nowrap border-l border-t border-slate-200 px-3 py-3 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                getAmountToneClass(result.acumulado)
              )}
            >
              {formatReportAmount(result.acumulado)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
